import { EntityName } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CryptoIcon,
  LineChart,
  PercentageValue,
  formatNumber,
  formatPercentage
} from '@blockanalitica/ui';
import { useCallback, useMemo } from 'react';

export default function HistoricGraphs({
  data,
  timeFilter,
  activeDisplayOption,
  chartDaysAgo
}) {
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);

  const yScaleValueFormatter = useCallback(
    (value: number) => {
      switch (activeDisplayOption) {
        case 'rate':
          return `${formatPercentage(value)}`;
        case 'utilization':
          return `${formatPercentage(value)}`;
        default:
          return `${formatNumber(value)}`;
      }
    },
    [activeDisplayOption]
  );

  const valueFormatter = useCallback(
    (value: string) => {
      switch (activeDisplayOption) {
        case 'rate':
          return <PercentageValue value={Number(value)} />;
        case 'utilization':
          return <PercentageValue value={Number(value)} />;
        default:
          return (
            <EntityName
              symbol2={<CryptoIcon name="DAI" size="3" />}
              symbol1={
                <CryptoIcon
                  name="USDS"
                  address={tokensMapping('USDS').address}
                  size="3"
                />
              }>
              {formatNumber(Number(value))}
            </EntityName>
          );
      }
    },
    [activeDisplayOption]
  );

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'rate':
        return [
          {
            label: 'DAI Savings Rate',
            borderColor: 'rgba(251, 200, 84, 1)',
            xField: 'date',
            yField: 'dsr_rate'
          },
          {
            label: 'Sky Savings Rate',
            borderColor: 'rgba(255, 139, 91, 1)',
            xField: 'date',
            yField: 'ssr_rate'
          }
        ];
      default:
        return [
          {
            xField: 'date',
            yField: activeDisplayOption
          }
        ];
    }
  }, [activeDisplayOption]);

  const config = updateChartConfigWithPeriod(
    //@ts-ignore
    activeDisplayOption === 'rate' ? { options: { fill: false } } : {}
  );

  return (
    <LineChart
      key={activeDisplayOption}
      data={data}
      datasets={datasets}
      valueFormatter={valueFormatter}
      yScaleValueFormatter={yScaleValueFormatter}
      timeFilter={timeFilter}
      config={config}
    />
  );
}
